/* eslint-disable eqeqeq */
import {
  PageStyleType,
  BoxLayer,
  BoxType,
  AssetType,
  CollectionType,
  PageConstants,
  Common,
  Numbers,
} from '@taopix/taopix-core';
import {PageItem, ShuffleLayout} from '@taopix/taopix-core/dist/types/types';

export interface PageLoadedObject {
  [key: string]: any;
  spread: PicturePageObject;
  left: PicturePageObject;
  right: PicturePageObject;
}

export interface AutoLayoutPageStyleList {
  [key: string]: any;
}

export interface AutoLayoutPageStyle {
  ref: number;
  type: number;
  portraitboxes: number;
  landscapeboxes: number;
  squareboxes: number;
  emptypictureboxcount?: string;
  hasgrid?: boolean;
}

export interface PicturePageObject {
  total: number;
  portrait: number;
  landscape: number;
  square: number;
}

export interface PopulatedPictureBox {
  boxLeft: number | string;
  boxWidth: number | string;
  hiResWidth: number;
  hiResHeight: number;
}

export interface ShuffleMap {
  nextItemRef: number;
  populatedPictureBoxes: PopulatedPictureBox[];
  pictureToShuffleArray: PicturesToShuffle[];
}

export interface LastLayoutUsed {
  [key: string]: any;
  spread: number;
  left: number;
  right: number;
}

export interface PicturesToShuffle {
  ref: number;
  hiResPath: number | string;
}

export interface OrientationCountResult {
  pageSquareCount: number;
  overflowCount: number;
  orientationCount: number;
}

export interface SquareCountResult {
  overflowCount: number;
  orientationCount: number;
}

export interface PageStyleWeight {
  index: number;
  key: string;
}

export const isPageStyleAvailable = (
  pageType: number,
  pageStyle: ShuffleLayout,
  pageHasGrid: boolean,
  productType: CollectionType
): boolean => {
  let addItem = true;
  if (pageType == PageStyleType.Single && pageStyle.type != PageStyleType.Single) {
    addItem = false;
  } else if (
    pageType == PageStyleType.Spread &&
    pageStyle.type != PageStyleType.Spread &&
    pageStyle.type != PageStyleType.Single
  ) {
    // A spread can use spread page styles or single page page styles.
    addItem = false;
  } else if (pageType == PageStyleType.Cover && pageStyle.type != PageStyleType.Cover) {
    addItem = false;
  }

  if (addItem) {
    // make sure the page style is compatible with the page the user is viewing.
    if (productType == CollectionType.Calendar && Boolean(pageStyle.hasgrid) != pageHasGrid) {
      addItem = false;
    }
  }
  return addItem;
};

export default class AutoLayout {
  private static readonly MINIMUM_LAYOUT_TO_SHUFFLE_LIST: number = 5;

  private pictureBoxesLoadedOnCurrentPage: PageLoadedObject = {
    spread: {total: 0, portrait: 0, landscape: 0, square: 0},
    left: {total: 0, portrait: 0, landscape: 0, square: 0},
    right: {total: 0, portrait: 0, landscape: 0, square: 0},
  };
  private LastLayoutUsed: LastLayoutUsed = {
    spread: 0,
    left: 0,
    right: 0,
  };
  private layoutShuffleList: AutoLayoutPageStyleList = [];
  private autoLayoutListLookUp: AutoLayoutPageStyleList = {
    spread: {},
    left: {},
    right: {},
  };
  private pictureToShuffleArray: PicturesToShuffle[] = [];
  private lastTypeDetected = -1;
  private foundDifferentPictures = false;

  public canShuffleSpreadPageLayout = (pPageStyleType: number): boolean => {
    return this.canShufflePageLayout('spread', pPageStyleType);
  };

  public canShuffleSinglePageLayout = (pPageType: string): boolean => {
    return this.canShufflePageLayout(pPageType, PageStyleType.Single);
  };

  public getShuffleListSize = (): number => {
    return this.layoutShuffleList.length;
  };

  public hasPictureOnPage = (pPageType: string): boolean => {
    return this.pictureBoxesLoadedOnCurrentPage[pPageType].total > 0;
  };

  public canShufflePicture = (): boolean => {
    return !(this.pictureToShuffleArray.length < 2 || !this.foundDifferentPictures);
  };

  public getPictureToShuffleArray = (): PicturesToShuffle[] => {
    return this.pictureToShuffleArray;
  };

  public getPictureToShuffleSize = (): number => {
    return this.pictureToShuffleArray.length;
  };

  public addItemToShuffleArray = (item: PicturesToShuffle): void => {
    this.pictureToShuffleArray.push(item);
  };

  /**
   * Set the list of all page styles available for the page.
   *
   * @param pPageStyleForShuffleList The list of pageStyles
   * @param pPageHasGrid True if the page has a grid.
   * @param pPageType Current page type.
   * @param pForceListCreation True if the existing list needs to be recreated.
   */
  public setLayoutList = (
    pPageStyleForShuffleList: ShuffleLayout[],
    pPageHasGrid: boolean,
    pPageType: number,
    productType: CollectionType,
    pForceListCreation: boolean
  ): void => {
    // Build the list of available page style if the type has changed compare to last time the list has been built.
    if (this.lastTypeDetected != pPageType || pForceListCreation) {
      // Build the list of available layout for the current page.
      this.layoutShuffleList = this.buildShuffleLayoutList(
        pPageStyleForShuffleList,
        pPageHasGrid,
        pPageType,
        productType
      );

      // Store the type of list generated.
      this.lastTypeDetected = pPageType;
    }

    // Clear look up if it was populated.
    this.autoLayoutListLookUp = [];
  };

  /**
   * Analyse items on page to determinate the number of picture boxes populated and empty.
   *
   * @param pPageItems The current page list list
   * @param pFromLayout True if the data need to be read from the product layout page item list.
   * @param pPageItemTempRef Page items next ref.
   * @param pPageNumber the page number
   * @param pLayoutWidth the product Layout Width
   * @param pSpreads True if the layout is spreads.
   * @returns Next item ref updated.
   */
  public setPictureBoxesOnCurrentPage = <T extends PageItem = PageItem>(
    pPageItems: T[],
    pPageItemTempRef: number,
    pPageNumber: number,
    pLayoutWidth: number,
    pSpreads: boolean
  ): number => {
    // Get the picture data for the current page.
    const getPictureBoxesResult: ShuffleMap = this.getPictureBoxesOncurrentPage(
      pPageItems,
      pPageItemTempRef
    );

    // Get the list of orientation pictures for the current pae.
    const pictureOrientationCount: PageLoadedObject = this.getPictureOrientationCount(
      getPictureBoxesResult.populatedPictureBoxes,
      pLayoutWidth,
      pPageNumber,
      pSpreads
    );

    // Check if pictures can be swapped by checking if there is more than one picture boxes and if pictures inside the boxes are not the same.
    if (getPictureBoxesResult.pictureToShuffleArray.length > 1) {
      const hiResPath = getPictureBoxesResult.pictureToShuffleArray[0].hiResPath;
      this.foundDifferentPictures = getPictureBoxesResult.pictureToShuffleArray.some(
        (pPicture: PicturesToShuffle): boolean => {
          return hiResPath !== pPicture.hiResPath;
        }
      );
    } else {
      this.foundDifferentPictures = false;
    }

    // If the number of picture or a shape of a picture have changed we need to reinitialise the index for shuffle layout.
    // And clear the specific lookup.
    if (
      this.checkPagePictureChanges(
        this.pictureBoxesLoadedOnCurrentPage.spread,
        pictureOrientationCount.spread
      )
    ) {
      // Reset spread allocation index.
      this.LastLayoutUsed.spread = 0;

      // Remove spread from lookup.
      delete this.autoLayoutListLookUp.spread;
    }

    if (
      this.checkPagePictureChanges(
        this.pictureBoxesLoadedOnCurrentPage.left,
        pictureOrientationCount.left
      )
    ) {
      this.LastLayoutUsed.left = 0;

      // Remove left from lookup.
      delete this.autoLayoutListLookUp.left;
    }

    if (
      this.checkPagePictureChanges(
        this.pictureBoxesLoadedOnCurrentPage.right,
        pictureOrientationCount.right
      )
    ) {
      this.LastLayoutUsed.right = 0;

      // Remove right from lookup.
      delete this.autoLayoutListLookUp.right;
    }

    // Store the list of pictures on page.
    this.pictureBoxesLoadedOnCurrentPage = pictureOrientationCount;
    this.pictureToShuffleArray = getPictureBoxesResult.pictureToShuffleArray;

    return getPictureBoxesResult.nextItemRef;
  };

  /**
   * Return the ref of the layout which needs to be applied.
   *
   * @param pPageStyleType Page style type
   * @param pApplyToType Page type requested.
   * @param pFromShuffle True if it's shuffle action.
   * @returns Ref of the layout which needs to be applied or -1 if none found.
   */
  public getValidLayout = (
    pPageStyleType: number,
    pApplyToType: string,
    pFromShuffle: boolean
  ): number => {
    let refToApply = -1;

    // Build the lookup list for the page type requested.
    this.setLayoutListForAPageType(pApplyToType, pPageStyleType, pFromShuffle);

    // Make sure we've got at least one page style available.
    const autoLayoutList: AutoLayoutPageStyle[] = this.autoLayoutListLookUp[pApplyToType];

    const countLayout: number = autoLayoutList.length;
    if (countLayout > 0) {
      // Reset the last use count if the list has been fully used or the list has changed.
      if (this.LastLayoutUsed[pApplyToType] === countLayout) {
        this.LastLayoutUsed[pApplyToType] = 0;
      }

      // Get the ref of the layout.
      refToApply = autoLayoutList[this.LastLayoutUsed[pApplyToType]].ref;

      // we use the next layout available
      this.LastLayoutUsed[pApplyToType]++;
    }

    return refToApply;
  };

  /**
   * Build a list of pages style which are matching the content of a page.
   *
   * @param pApplyToType Page type requested.
   * @param pPageStyleType Expected page style type.
   * @return A list of page style compatible with the current page type.
   */
  public buildPageStyleLayoutList = (
    pApplyToType: string,
    pPageStyleType: number
  ): AutoLayoutPageStyle[] => {
    let autoLayoutList: AutoLayoutPageStyle[] = [];
    const picturesOnPage: PicturePageObject = this.pictureBoxesLoadedOnCurrentPage[pApplyToType];
    const shuffleLayoutList: AutoLayoutPageStyle[] = this.layoutShuffleList[picturesOnPage.total];
    // Make sure we got a page style with the required number of picture boxes.
    if (shuffleLayoutList !== undefined) {
      autoLayoutList = this.getBestMatchList(
        autoLayoutList,
        picturesOnPage,
        shuffleLayoutList,
        pPageStyleType
      );
    }

    // Not enough page style have been found we are trying to find alternatives.
    if (autoLayoutList.length < AutoLayout.MINIMUM_LAYOUT_TO_SHUFFLE_LIST) {
      // Loop around alternative layout to find the one wich will match the best.
      for (const emptyPictureBoxCount in this.layoutShuffleList) {
        // Functions associated to the array need to be filtered.
        // Look for an alternative with an highest number of pictures.
        if (
          typeof this.layoutShuffleList[emptyPictureBoxCount] === 'object' &&
          Number(emptyPictureBoxCount) > picturesOnPage.total
        ) {
          // Get best match with more boxes.
          autoLayoutList = this.getBestMatchList(
            autoLayoutList,
            picturesOnPage,
            this.layoutShuffleList[emptyPictureBoxCount],
            pPageStyleType
          );

          // Stop if we have found enough alternatives.
          if (autoLayoutList.length >= AutoLayout.MINIMUM_LAYOUT_TO_SHUFFLE_LIST) {
            break;
          }
        }
      }
    }

    return autoLayoutList;
  };

  /**
   * Compare two page picture settings.
   *
   * @param pCurrentPageSetting Current page setting.
   * @param pNewPageSettings New page setting.
   * @returns True if the two settings are differents.
   */
  private checkPagePictureChanges = (
    pCurrentPageSetting: PicturePageObject,
    pNewPageSettings: PicturePageObject
  ): boolean => {
    return (
      pCurrentPageSetting.total !== pNewPageSettings.total ||
      pCurrentPageSetting.landscape !== pNewPageSettings.landscape ||
      pCurrentPageSetting.portrait !== pNewPageSettings.portrait ||
      pCurrentPageSetting.square !== pNewPageSettings.square
    );
  };

  /**
   * Analyse items on page to determinate the number of picture boxes populated and empty.
   *
   * @param pPageItems The current page item list
   * @param pFromLayout True if the data need to be read from the product layout page item list.
   * @param pPageItemTempRef Page items next ref.
   * @param pPageNumber the current page number
   * @param pLayoutWidth  the product layout width
   * @returns List of populated picture boxes, list of all picture boxes available on page and the next item ref.
   */
  private getPictureBoxesOncurrentPage = <T extends PageItem = PageItem>(
    pPageItems: T[],
    pPageItemTempRef: number
  ): ShuffleMap => {
    let nextItemRef: number = pPageItemTempRef;
    const populatedPictureBoxes: PopulatedPictureBox[] = [];
    const pictureToShuffleArray: PicturesToShuffle[] = [];
    const pageItemList = pPageItems;

    const countPageItems: number = pageItemList.length;

    // Loop around all page items to get the number of populated picture boxes.
    for (let i = 0; i < countPageItems; i++) {
      const pageItem = pageItemList[i];

      // Exclude grid object for calendar.
      if (
        !Common.isCalendarGridObject(pageItem.name) &&
        (pageItem.layer === BoxLayer.Normal || pageItem.layer === BoxLayer.NonPrintable) &&
        pageItem.type === BoxType.Image &&
        pageItem.protectcontents === 0
      ) {
        // Add the box, to the shuffle list if it's a user image or the box is empty.
        if (pageItem.hiresfiletype === AssetType.User || pageItem.hirespath === '') {
          pictureToShuffleArray.push({
            ref: pageItem.ref,
            hiResPath: pageItem.hirespath === '' ? '' : (Number(pageItem.hirespath) as number),
          });
        }

        // Store populated picture boxes.
        if (pageItem.hirespath !== '' && pageItem.hiresfiletype === AssetType.User) {
          populatedPictureBoxes.push({
            boxLeft: pageItem.boxleft,
            boxWidth: pageItem.boxwidth,
            hiResWidth: pageItem.hireswidth,
            hiResHeight: pageItem.hiresheight,
          });
        }
      }

      // Make sure the minimum ref is not already used.
      // Not sure how this can be tiggered, we need to investigate if the code is still needed.
      if (pageItem.ref <= nextItemRef) {
        nextItemRef = pageItem.ref - 1;
      }
    }

    return {
      nextItemRef: nextItemRef,
      pictureToShuffleArray: pictureToShuffleArray,
      populatedPictureBoxes: populatedPictureBoxes,
    };
  };

  /**
   * Return each asset orientations' count for a page.
   *
   * @param pPopulatedBoxes List of populated picture boxes.
   * @param pLayoutWidth The layout width.
   * @param pageNumber page number we are applying to
   * @param spreads True if the product is spreads.
   * @return Each asset orientations' count for a page.
   */
  private getPictureOrientationCount = (
    pPopulatedBoxes: PopulatedPictureBox[],
    pLayoutWidth: number,
    pageNumber: number,
    spreads: boolean
  ): PageLoadedObject => {
    let spreadPagePictureCount: PicturePageObject = {
      total: 0,
      portrait: 0,
      landscape: 0,
      square: 0,
    };
    let leftPagePictureCount: PicturePageObject = {total: 0, portrait: 0, landscape: 0, square: 0};
    let rightPagePictureCount: PicturePageObject = {total: 0, portrait: 0, landscape: 0, square: 0};
    const populatedBoxesLength: number = pPopulatedBoxes.length;
    const rollOffset: string = Numbers.mul(pageNumber, pLayoutWidth);

    for (let i = 0; i < populatedBoxesLength; i++) {
      const populatedPictureBox: PopulatedPictureBox = pPopulatedBoxes[i];
      let landscape = 0;
      let portrait = 0;
      let square = 0;

      // Get the picture orientation.
      if (populatedPictureBox.hiResWidth > populatedPictureBox.hiResHeight * 1.1) {
        landscape++;
      } else if (populatedPictureBox.hiResHeight > populatedPictureBox.hiResWidth * 1.1) {
        portrait++;
      } else {
        square++;
      }

      /* Store the number of pictures for each page side and spread.
       * Originally, this was just checking for the boxLeft of the image, but this means that if there are any pageStyles on the
       * right side of the page that have pictures hanging over the centre then they will be treated as though they are on left
       * side of the page.  Therefore, instead we should decide which page an image is on by using the centre of the image instead
       * of just the boxLeft
       * (Original: TaopixBigNumber.cmp(populatedPictureBox.boxLeft, pLayoutWidth) !== -1)
       */
      if (
        [PageConstants.FrontCoverPageNumber, PageConstants.BackCoverPageNumber].indexOf(
          pageNumber
        ) < 0 &&
        Numbers.cmp(
          Numbers.add(
            Numbers.sub(populatedPictureBox.boxLeft, rollOffset),
            Numbers.div(populatedPictureBox.boxWidth, 2)
          ),
          pLayoutWidth
        ) === 1 &&
        spreads
      ) {
        rightPagePictureCount = this.setPictureOrientationCount(
          rightPagePictureCount,
          landscape,
          portrait,
          square
        );
      } else {
        leftPagePictureCount = this.setPictureOrientationCount(
          leftPagePictureCount,
          landscape,
          portrait,
          square
        );
      }

      if (spreads) {
        spreadPagePictureCount = this.setPictureOrientationCount(
          spreadPagePictureCount,
          landscape,
          portrait,
          square
        );
      }
    }

    return {
      spread: spreadPagePictureCount,
      left: leftPagePictureCount,
      right: rightPagePictureCount,
    };
  };

  /**
   * Change the picture orientation total count.
   *
   * @param pPageObject Page object to be changed.
   * @param pLandscape Landscape value to increase.
   * @param pPortrait Portrait value to increase.
   * @param pSquare Square value to increase.
   * @return Object with the orientation count increased.
   */
  private setPictureOrientationCount = (
    pPageObject: PicturePageObject,
    pLandscape: number,
    pPortrait: number,
    pSquare: number
  ): PicturePageObject => {
    return {
      total: pPageObject.total + 1,
      portrait: pPageObject.portrait + pPortrait,
      landscape: pPageObject.landscape + pLandscape,
      square: pPageObject.square + pSquare,
    };
  };

  /**
   * Build the list of available layout for a page type.
   *
   * @param pPageStyleForShuffleList list of page styles
   * @param pPageHasGrid True if a grid exists on page.
   * @param pPageType Page type to be looked at.
   * @return List of page style witch are available for the current page.
   */
  private buildShuffleLayoutList = (
    pPageStyleForShuffleList: ShuffleLayout[],
    pPageHasGrid: boolean,
    pPageType: number,
    productType: CollectionType
  ): AutoLayoutPageStyleList => {
    const layoutShuffleList: AutoLayoutPageStyleList = [];

    pPageStyleForShuffleList.forEach(pageStyle => {
      // Make sure the layout is compatible with the current page.
      if (isPageStyleAvailable(pPageType, pageStyle, pPageHasGrid, productType)) {
        // Check if an entry for this amount of pictures exists if not add one.
        if (typeof layoutShuffleList[pageStyle.emptypictureboxcount] === 'undefined') {
          layoutShuffleList[pageStyle.emptypictureboxcount] = [];
        }

        // Add the valid page style to the list.
        layoutShuffleList[pageStyle.emptypictureboxcount].push({
          ref: pageStyle.ref,
          type: pageStyle.type,
          portraitboxes: pageStyle.portraitboxes,
          landscapeboxes: pageStyle.landscapeboxes,
          squareboxes: pageStyle.squareboxes,
        });
      }
    });
    return layoutShuffleList;
  };

  /**
   * Build a lookup list of page styles compatible with the page type passed by parameter.
   *
   * @param pApplyToType Page type requested.
   * @param pPageStyleType Page style type
   * @param pFromShuffle True if it's shuffle action.
   */
  private setLayoutListForAPageType = (
    pApplyToType: string,
    pPageStyleType: number,
    pFromShuffle: boolean
  ): void => {
    // Check if the list has already been built for the requested configuration.
    if (this.autoLayoutListLookUp[pApplyToType] === undefined || !pFromShuffle) {
      // Build the list for the new configuartaion.
      this.autoLayoutListLookUp[pApplyToType] = this.buildPageStyleLayoutList(
        pApplyToType,
        pPageStyleType
      );
    }
  };

  /**
   * Return a list of page style best matches for the current page content.
   *
   * @param pBestMatchList List of existing best matches to be completed.
   * @param pPicturesOnPage Picture on page configuration.
   * @param pLayoutList List of layout to be looked at.
   * @param pPageStyleType Expected page style type.
   * @returns List of page style wich are the best match for the current pagge type requested.
   */
  private getBestMatchList = (
    pBestMatchList: AutoLayoutPageStyle[],
    pPicturesOnPage: PicturePageObject,
    pLayoutList: AutoLayoutPageStyle[],
    pPageStyleType: number
  ): AutoLayoutPageStyle[] => {
    const autoLayoutList: AutoLayoutPageStyle[] = pBestMatchList;
    let iterationIndex = 0;

    // Build weighted lsit
    const weightedList: AutoLayoutPageStyle[][] = this.buildWeightedList(
      pPicturesOnPage,
      pLayoutList,
      pPageStyleType
    );
    const weightedListLength: number = weightedList.length;

    // Try to find a list of matching number picture on page.
    while (
      autoLayoutList.length < AutoLayout.MINIMUM_LAYOUT_TO_SHUFFLE_LIST &&
      iterationIndex < weightedListLength
    ) {
      // eslint-disable-next-line prefer-spread
      autoLayoutList.push.apply(autoLayoutList, weightedList[iterationIndex]);
      iterationIndex++;
    }

    return autoLayoutList;
  };

  /**
   * Return a weighted list sorted by match.
   *
   * @param pPicturesOnPage Picture on page configuration.
   * @param pLayoutList List of layout to be looked at.
   * @param pPageStyleType Expected page style type.
   * @returns List of sorted page style by weigth.
   */
  private buildWeightedList = (
    pPicturesOnPage: PicturePageObject,
    pLayoutList: AutoLayoutPageStyle[],
    pPageStyleType: number
  ): AutoLayoutPageStyle[][] => {
    const weightedList: AutoLayoutPageStyle[][] = [];
    const weightedPageStylesList: AutoLayoutPageStyle[] = [];
    const pageStyleWeightList: PageStyleWeight[] = [];
    let previousKey = '';
    let weightedListIndex = -1;
    let weightedIndex = 0;
    const countTempList: number = pLayoutList.length;

    for (let i = 0; i < countTempList; i++) {
      const layout: AutoLayoutPageStyle = pLayoutList[i];

      // Respect the time and make sure the object is not in the list already.
      if (layout.type === pPageStyleType) {
        // Determine how many landscape boxes match the landscape picture count.
        const landScapeCountResult: OrientationCountResult = this.getOrientationCount(
          pPicturesOnPage.landscape,
          pPicturesOnPage.square,
          0,
          layout.landscapeboxes
        );

        // Determine how many portrait boxes match the portrait picture count.
        const portraitCountResult: OrientationCountResult = this.getOrientationCount(
          pPicturesOnPage.portrait,
          landScapeCountResult.pageSquareCount,
          landScapeCountResult.overflowCount,
          layout.portraitboxes
        );

        // Determine how many square boxes match the square picture count.
        // We also include the unfullfilled count from other orientations because square is better than the opposite orientation.
        const squareCountResult: SquareCountResult = this.getSquareCount(
          portraitCountResult.pageSquareCount,
          portraitCountResult.overflowCount,
          layout.squareboxes
        );

        // Store the weighted page style information.
        weightedPageStylesList.push(layout);
        pageStyleWeightList.push({
          index: weightedIndex,
          key: this.buildWeightString(
            landScapeCountResult.orientationCount,
            portraitCountResult.orientationCount,
            squareCountResult.orientationCount,
            squareCountResult.overflowCount
          ),
        });
        weightedIndex++;
      }
    }

    // Sort the array by weight.
    pageStyleWeightList.sort((pItem1: PageStyleWeight, pItem2: PageStyleWeight) => {
      let returnValue = 0;

      if (pItem1.key < pItem2.key) {
        returnValue = -1;
      }

      if (pItem1.key > pItem2.key) {
        returnValue = 1;
      }
      return returnValue;
    });

    // Build the list of best matches.
    const pageStyleWeightListLength: number = pageStyleWeightList.length;
    for (let i = 0; i < pageStyleWeightListLength; i++) {
      const pageStyleWeight: PageStyleWeight = pageStyleWeightList[i];

      if (previousKey !== pageStyleWeight.key) {
        previousKey = pageStyleWeight.key;
        weightedListIndex++;
      }

      if (weightedList[weightedListIndex] === undefined) {
        weightedList[weightedListIndex] = [];
      }

      weightedList[weightedListIndex].push(weightedPageStylesList[pageStyleWeight.index]);
    }

    return weightedList;
  };

  /**
   * Returns how many specific orientation boxes match the orientation picture count.
   *
   * @param pOrientationCount Expected picture count of the orientation tested.
   * @param pPageSquareCountToFind Existing square picture count into a page.
   * @param pOrientationOverflowCount Existing overflow picture count.
   * @param pOrientationBoxCount The orientation picture box count into the page style.
   * @returns Overflow count, orientation box count and a page style square box count.
   */
  private getOrientationCount = (
    pOrientationCount: number,
    pPageSquareCountToFind: number,
    pOrientationOverflowCount: number,
    pOrientationBoxCount: number
  ): OrientationCountResult => {
    let orientationCount: number = pOrientationCount - pOrientationBoxCount;
    let pageSquareCountToFind: number = pPageSquareCountToFind;
    let orientationOverflowCount: number = pOrientationOverflowCount;

    // Update the stats for the orientation.
    if (orientationCount > 0) {
      // We have pictures remaining so add the remainder to the square count
      pageSquareCountToFind += orientationCount;
    } else if (orientationCount < 0) {
      // Set the picture count to the overflow count
      orientationCount = Math.abs(orientationCount);

      // We have empty boxes remaining so add the remainder to the overflow count.
      orientationOverflowCount += orientationCount;
    }

    return {
      pageSquareCount: pageSquareCountToFind,
      overflowCount: orientationOverflowCount,
      orientationCount: orientationCount,
    };
  };

  /**
   * Determine how many square boxes match the square picture count.
   *
   * @param pSquareCountToFind Existing square picture count.
   * @param pOrientationOverflowCount Existing overflow picture count.
   * @param pSquareBoxCount Square picture box count into the page style.
   * @returns Square box count and overflow count.
   */
  private getSquareCount = (
    pSquareCountToFind: number,
    pOrientationOverflowCount: number,
    pSquareBoxCount: number
  ): SquareCountResult => {
    // Determine how many square boxes match the square picture count.
    // We also include the unfullfilled count from other orientations because square is better than the opposite orientation.
    let squareCount: number = pSquareCountToFind - pSquareBoxCount;
    let orientationOverflowCount: number = pOrientationOverflowCount;

    // Update the stats for the square orientation.
    if (squareCount < 0) {
      // Set the picture count to the overflow count.
      squareCount = Math.abs(squareCount);

      // We have empty boxes remaining so add the remainder to the overflow count.
      orientationOverflowCount += squareCount;
    }

    return {
      overflowCount: orientationOverflowCount,
      orientationCount: squareCount,
    };
  };

  /**
   * Build a key that can be used to weight the page style.
   * - This is in the format of 4 sets of counts separated by an underscore (landscape_portrait_square_overflow).
   * - The landscape and portrait counts represent the number of pictures of that orientation that didn't have a picture box matching that orientation.
   * - The square count represents the number of square + remaining other orientation pictures that didn't have a square picture box.
   * - The overflow count represents the total number of picture boxes that had orientations that weren't filled by pictures.
   *
   * @param pLandscapeCount Number of landscape picture boxes.
   * @param pPortraitCount Number of portrait picture boxes.
   * @param pSquareCount Number of square picture boxes.
   * @param pOverflowCount Number of remaining empty picture boxes.
   * @returns The weight string.
   */
  private buildWeightString = (
    pLandscapeCount: number,
    pPortraitCount: number,
    pSquareCount: number,
    pOverflowCount: number
  ): string => {
    // Build a key that can be used to weight the page style.
    // This is in the format of 4 sets of counts separated by an underscore (landscape_portrait_square_overflow).
    // The landscape and portrait counts represent the number of pictures of that orientation that didn't have a picture box matching that orientation.
    // The square count represents the number of square + remaining other orientation pictures that didn't have a square picture box.
    // The overflow count represents the total number of picture boxes that had orientations that weren't filled by pictures.
    let weightString: string = String('0000' + pLandscapeCount).slice(-4) + '_';
    weightString += String('0000' + pPortraitCount).slice(-4) + '_';
    weightString += String('0000' + pSquareCount).slice(-4) + '_';
    weightString += String('0000' + pOverflowCount).slice(-4);

    return weightString;
  };

  private canShufflePageLayout = (pPageType: string, pPageStyleType: number): boolean => {
    this.setLayoutListForAPageType(pPageType, pPageStyleType, true);
    return this.autoLayoutListLookUp[pPageType].length > 1;
  };
}
